import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link2, Download } from 'lucide-react';
import { AudioPlayer } from '@/components/common/AudioPlayer';
import { ImageLightbox } from '@/components/common/ImageLightbox';

interface Props {
  clienteId?: string;
  pedidoId?: string;
}

interface Prova {
  key: string;
  url: string;
  isAudio: boolean;
  numeroPedido?: string;
}

const AUDIO_RE = /\.(mp3|wav|ogg|m4a|aac|opus|webm)(\?|$)/i;

// Espelho READ-ONLY das provas anexadas no fechamento da convergência.
// Origem: pedidos.convergencia.prova_urls (bucket whatsapp-media). Não são
// linhas de anexos_crm — por isso aparecem numa seção própria, sem excluir.
export function ConvergenciaProvasList({ clienteId, pedidoId }: Props) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const { data: provas, isLoading } = useQuery({
    queryKey: ['convergencia-provas', clienteId, pedidoId],
    enabled: !!(clienteId || pedidoId),
    queryFn: async () => {
      let query = supabase.from('pedidos').select('id, numero_pedido, convergencia');

      if (clienteId) {
        query = query.eq('cliente_id', clienteId);
      } else if (pedidoId) {
        const { data: ped } = await supabase
          .from('pedidos')
          .select('pedido_mae_id')
          .eq('id', pedidoId)
          .single();
        if (ped?.pedido_mae_id) {
          query = query.or(`id.eq.${pedidoId},id.eq.${ped.pedido_mae_id}`);
        } else {
          query = query.eq('id', pedidoId);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const out: Prova[] = [];
      for (const p of data || []) {
        const conv: any = p.convergencia || {};
        const urls: string[] =
          Array.isArray(conv.prova_urls) && conv.prova_urls.length
            ? conv.prova_urls
            : conv.prova_url
              ? [conv.prova_url]
              : [];
        urls.forEach((url, i) => {
          if (!url) return;
          out.push({
            key: `${p.id}-${i}`,
            url,
            isAudio: AUDIO_RE.test(url),
            numeroPedido: p.numero_pedido != null ? String(p.numero_pedido) : undefined,
          });
        });
      }
      return out;
    },
  });

  if (isLoading || !provas || provas.length === 0) return null;

  return (
    <>
      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Link2 className="h-4 w-4 text-primary" />
          Provas de Convergência
          <Badge variant="secondary" className="text-xs">{provas.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Prints e gravações anexados no fechamento da convergência (somente leitura).
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {provas.map((prova) => (
            <Card key={prova.key}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="text-xs">
                    {prova.isAudio ? 'Gravação' : 'Print'}
                    {prova.numeroPedido ? ` · #${prova.numeroPedido}` : ''}
                  </Badge>
                  <a
                    href={prova.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    title="Baixar"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
                {prova.isAudio ? (
                  <AudioPlayer src={prova.url} className="w-full" />
                ) : (
                  <button
                    type="button"
                    onClick={() => setLightboxUrl(prova.url)}
                    className="block w-full"
                  >
                    <img
                      src={prova.url}
                      alt="Prova de convergência"
                      className="max-h-48 w-full object-contain rounded-md border bg-muted/20"
                    />
                  </button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <ImageLightbox src={lightboxUrl} alt="Prova de convergência" onClose={() => setLightboxUrl(null)} />
    </>
  );
}
